import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Input,
  Button,
  Table,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";

import BreadCrumb from "Components/Common/BreadCrumb";
import Loader from "Components/Common/Loader";

import type { RootState } from "store";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";

import { fetchDoorstepRequests } from "slices/deliverycharge/thunk";
import { formatDeliveryDate, getStatusStyle } from "./deliveryHelper";
import { useNavigate } from "react-router-dom";
import DeliveryContainer from "./deliverycontainer";
        
const selectDeliveryState = (state: RootState) => (state as any).doorstepdelivery;

const deliverySelector = createSelector(selectDeliveryState, (dc) => ({
  requests: dc.deliveryRequests,
  loading: dc.loading,
  pagination: dc.deliveryRequestsPagination,
}));

const DeliveryPage: React.FC = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const { requests, loading, pagination } = useSelector(deliverySelector);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const loadRequests = useCallback(() => {
    return dispatch(
      fetchDoorstepRequests({
        page,
        page_size: pageSize,
        requestid: null,
        delivstatus: statusFilter,
        fromdate: null,
        todate: null,
        searchtext: searchQuery?.trim() || null,
      })
    );
  }, [dispatch, statusFilter, page, pageSize, searchQuery]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  // Server-side filtering/pagination
  const filteredRequests = requests;

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "requestid",
        cell: ({ row }: any) => `#ID${row.original.requestid ?? ""}`,
      },
      { header: "Booked by", accessorKey: "bookedby", 
      cell: ({ row }: any) =>( 
      <div style={{ textAlign: "center" }}>
         {row.original.bookedby || "-"}
      </div>)
      },
      {
        header: "Beneficiary name",
        accessorKey: "benficiaryname",
        cell: ({ row }: any) => {
          const name = row.original.benficiaryname;
      
          const formattedName = name
            ? name.charAt(0).toUpperCase() + name.slice(1)
            : "-";
      
          return (
            <div style={{ textAlign: "left" }}>
              {formattedName}
            </div>
          );
        },
      },      
      { header: "Mobile Number", accessorKey: "benficiarymobile" },
      {
        header: "Date",
        accessorKey: "deliverydate",
        cell: ({ row }: any) => formatDeliveryDate(row.original.deliverydate),
      },
      { header: "Time slot", accessorKey: "timeslot" },
      {
        header: "Amount",
        accessorKey: "amount",
        cell: ({ row }: any) =>
          row.original.amount ? `BD ${row.original.amount}` : "—",
      },
      {
        header: "Charges",
        accessorKey: "charge",
        cell: ({ row }: any) => (
          <div style={{ textAlign: "center" }}>
            {row.original.charge ? `BD ${row.original.charge}` : "—"}
          </div>
        ),
      },
      {
        header: "Status",
        accessorKey: "delivstatus",
        cell: ({ row }: any) => renderStatusPill(row.original.delivstatus),
      },
      {
        header: "DeliveryAddress",
        accessorKey: "deliveryaddress",
        cell: ({ row }: any) => (
          <div
            style={{
              textAlign: "left",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              wordBreak: "break-word",
            }}
            title={row.original.deliveryaddress}
          >
            {row.original.deliveryaddress || "—"}
          </div>
        ),
      }, 
      {
        header: "Agent Name",
        accessorKey: "agentname",
        cell: ({ row }: any) => (
          <div style={{ textAlign: "center" }}>
            {row.original.agentname || "-"}
          </div>
        ),
      },
      {
        header: "Agent Phone",
        accessorKey: "agentphone",
        cell: ({ row }: any) => (
        <div style={{ textAlign: "center" }}>
          {row.original.agentphone || "-"}
        </div>),
      },
      {
        header: "Cancel Reason",
        accessorKey: "reason",
        cell: ({ row }: any) => (
        <div style={{ textAlign: "center" }}>
          {row.original.reason || "-"}
        </div>),
      }
    ],
    []
  );

  const renderStatusPill = (status?: string) => {
    const { color, bg } = getStatusStyle(status);
    const label = status || "-";
    return (
      <span
        style={{
          display: "inline-block",
          minWidth: 92,
          textAlign: "center",
          padding: "6px 12px",
          borderRadius: "16px",
          color,
          background: bg,
          textTransform: "capitalize",
          fontWeight: 600,
          fontSize: "12px",
        }}
      >
        {label}
      </span>
    );
  };

  return (
    <div className="page-content">
      <Container fluid className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4">
        <BreadCrumb title="Doorstep delivery" pageTitle={["Doorstep delivery"]} />

        <Row className="align-items-center g-3 d-flex justify-content-between pb-4 pe-3">
          <Col md={2}>
            <div className="position-relative">
            <Input
                type="text"
                className="form-control border ps-5 ms-2 rounded-3"
                value={""}
                placeholder="Filter"
                disabled
            />
            <i
                className="ri-filter-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3"
                style={{ fontSize: "1.1rem" }}
            />
            </div>
         </Col>
          <Col md={3}>
            <div className="position-relative">
            <Input
                type="text"
                className="form-control border ps-5 ms-2 rounded-3"
                value={searchQuery}
                placeholder="Search Delivery Requests..."
                onChange={(e) => {
                  setPage(1);
                  setSearchQuery(e.target.value);
                }}
            />
            <i
                className="ri-search-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3"
                style={{ fontSize: "1.1rem" }}
            />
            </div>
         </Col>
          <Col md={7} className="text-end d-flex justify-content-end gap-2 align-items-center">
            <Button color="primary"  outline onClick={() => navigate('/deliverycharges')}>
              <i className="ri-stack-line align-middle me-1" />
                View Delivery Charges
            </Button>
          </Col>    
        </Row>

        <Row>
          <Col lg={12}>
            <Card className="border-0 shadow-none">
              <CardBody>
                {!loading ? (
                  filteredRequests.length > 0 ? (
                    <DeliveryContainer
                      columns={columns}
                      data={filteredRequests}
                      pagination={pagination || undefined}
                      onPageChange={(p: number) => setPage(p)}
                      tableClass="align-middle border-bottom rounded-pill"
                      theadClass="bg-primary text-white border-top border-success"
                      divClass="table-responsive table-card"
                    />
                  ) : (
                    <div className="text-center p-4">No records found.</div>
                  )
                ) : (
                  <Loader />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default DeliveryPage;

